import { dashboardApi } from '@/api';
import { ApiErrorDetail } from '@/api/types';
import { queryOptions } from '@tanstack/react-query';

interface DataFileSignedUrlQueryArgs {
    projectId: string;
    dashboardId: string | undefined;
    fileType?: 'input' | 'output' | 'eventlog';
    extension?: 'duckdb' | 'xlsx' | 'jsonl';
}

export interface DataFileSignedUrlResponse {
    signedUrl: string;
}

const getDataFileSignedUrl = async ({
    projectId,
    fileType = 'output',
    dashboardId,
    extension = 'duckdb',
}: DataFileSignedUrlQueryArgs) => {
    return dashboardApi<DataFileSignedUrlResponse>({
        method: 'GET',
        url: `/front-office/project/${projectId}/data/${fileType}`,
        params: {
            dashboardId,
            extension,
        },
    });
};

const getDuckDBSignedUrl = (
    args: Omit<DataFileSignedUrlQueryArgs, 'extension'>,
) => getDataFileSignedUrl({ ...args, extension: 'duckdb' });

const getExcelSignedUrl = (
    args: Omit<DataFileSignedUrlQueryArgs, 'extension'>,
) => getDataFileSignedUrl({ ...args, extension: 'xlsx' });

export const duckDBSignedUrlQueryOptions = (
    args: Omit<DataFileSignedUrlQueryArgs, 'extension'>,
    options?: { enabled?: boolean },
) =>
    queryOptions({
        queryKey: ['duckdb-signed-url', args],
        queryFn: () => getDuckDBSignedUrl(args),
        staleTime: Infinity,
        ...options,
    });

export const excelSignedUrlQueryOptions = (
    args: Omit<DataFileSignedUrlQueryArgs, 'extension'>,
    options?: { enabled?: boolean },
) =>
    queryOptions({
        queryKey: ['excel-signed-url', args],
        queryFn: () => getExcelSignedUrl(args),
        staleTime: Infinity,
        ...options,
    });

const getEventLogSignedUrl = (
    args: Omit<DataFileSignedUrlQueryArgs, 'extension' | 'fileType'>,
) =>
    getDataFileSignedUrl({
        ...args,
        fileType: 'eventlog',
        extension: 'jsonl',
    });

export const eventLogSignedUrlQueryOptions = (
    args: Omit<DataFileSignedUrlQueryArgs, 'extension' | 'fileType'>,
) =>
    queryOptions({
        queryKey: ['eventlog-signed-url', args],
        queryFn: async () => {
            try {
                return await getEventLogSignedUrl(args);
            } catch (error) {
                if ((error as ApiErrorDetail)?.statusCode === 404) {
                    return null;
                }
                throw error;
            }
        },
        staleTime: Infinity,
    });

export const dataFileSignedUrlQueryOptions = (
    args: DataFileSignedUrlQueryArgs,
    options?: { enabled?: boolean },
) =>
    queryOptions({
        queryKey: ['data-file-signed-url', args],
        queryFn: () => getDataFileSignedUrl(args),
        staleTime: Infinity,
        ...options,
    });
